'use client';

// Read everything ffmpeg knows about a media file, without uploading it.
//
// WHY THIS TOOL SHAPE: measured 2026-09-19 across 166 tool pages and ~1M impressions, tools whose
// answer needs a file the visitor has earn 2.81% CTR at average position 17.1, while tools whose
// answer is fully contained in the query text earn 0.12% at position 13.1. Twenty-three times the
// click-through from four positions worse. The reason is mechanical: if the query holds the whole
// input, the result page can answer it and the click has no purpose. Google cannot report the
// codec of a file it does not have, so an inspector is on the right side of that line.
//
// The parsing target is ffmpeg's `-i` stream table, which looks like this:
//
//   Input #0, mov,mp4,m4a,3gp,3g2,mj2, from 'input.mp4':
//     Metadata:
//       title           : Sample clip
//     Duration: 00:00:04.00, start: 0.000000, bitrate: 445 kb/s
//     Stream #0:0[0x1](und): Video: h264 (Constrained Baseline) (avc1 / 0x31637661), yuv420p, 1280x720 [SAR 1:1 DAR 16:9], 309 kb/s, 30 fps, 30 tbr, 15360 tbn (default)
//     Stream #0:1[0x2](und): Audio: aac (LC) (mp4a / 0x6134706D), 44100 Hz, mono, fltp, 125 kb/s (default)
//
// `-i` with no output always exits non-zero and prints this to the log, which is why the exit code
// is ignored throughout.

import { getBrowserFfmpegRuntime } from '@/lib/browser-gif-tools';

export type MediaStreamInfo = {
  index: string;
  kind: 'Video' | 'Audio' | 'Subtitle' | 'Data' | 'Attachment';
  codec: string;
  profile: string | null;
  /** Video only. */
  resolution: string | null;
  pixelFormat: string | null;
  frameRate: string | null;
  displayAspectRatio: string | null;
  /** Audio only. */
  sampleRate: string | null;
  channels: string | null;
  bitrate: string | null;
  language: string | null;
};

export type MediaInspectReport = {
  fileName: string;
  fileSize: number;
  container: string | null;
  durationMs: number | null;
  overallBitrate: string | null;
  streams: MediaStreamInfo[];
  tags: { name: string; value: string }[];
};

export function parseDurationMs(log: string): number | null {
  const m = /Duration:\s(\d+):(\d{2}):(\d{2})\.(\d{2})/.exec(log);
  if (!m) return null;
  return (Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3])) * 1000 + Number(m[4]) * 10;
}

// Container names come through as ffmpeg's demuxer list, e.g. "mov,mp4,m4a,3gp,3g2,mj2" for
// anything in the ISO base media family. Taking the first entry would report a plain .mp4 as
// "mov", which reads as a wrong answer to the person who just dropped the file in, so the entry
// matching the file's own extension wins when there is one.
export function parseContainer(log: string, fileName?: string): string | null {
  const m = /Input #0,\s([^\n]+?),\sfrom\s/.exec(log);
  if (!m) return null;

  const names = m[1]
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  if (names.length === 0) return null;

  const extension = fileName?.toLowerCase().split('.').pop() ?? '';
  return names.find((name) => name.toLowerCase() === extension) ?? names[0];
}

export function parseOverallBitrate(log: string): string | null {
  return /Duration:[^\n]*?bitrate:\s([\d.]+\s*[kM]?b\/s)/.exec(log)?.[1]?.trim() ?? null;
}

// Top-level container tags only. Per-stream Metadata blocks are indented deeper and are mostly
// handler_name / vendor_id noise, so they are deliberately not collected.
export function parseTags(log: string): { name: string; value: string }[] {
  const lines = log.split('\n');
  const tags: { name: string; value: string }[] = [];
  let inBlock = false;

  for (const line of lines) {
    if (/^\s{2}Metadata:\s*$/.test(line)) {
      inBlock = true;
      continue;
    }
    if (inBlock) {
      const m = /^\s{4}(\S[^:]*?)\s*:\s(.*)$/.exec(line);
      if (m) {
        const name = m[1].trim();
        const value = m[2].trim();
        if (value && !/^handler_name|^vendor_id/.test(name)) {
          tags.push({ name, value });
        }
        continue;
      }
      inBlock = false;
    }
  }

  return tags;
}

export function parseStreams(log: string): MediaStreamInfo[] {
  const streams: MediaStreamInfo[] = [];
  const re =
    /Stream #(\d+:\d+)(?:\[[^\]]*\])?(?:\(([^)]*)\))?:\s(Video|Audio|Subtitle|Data|Attachment):\s([^\n]+)/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(log)) !== null) {
    const [, index, language, kind, rest] = match;

    // The codec name is the first token; a parenthesised profile may follow it.
    const codec = rest.split(/[\s,(]/)[0] ?? 'unknown';
    const profileMatch = /^\S+\s+\(([^)]+)\)/.exec(rest);
    // The second parenthesis on a video line is the fourcc, not a profile, so it is skipped.
    const profile =
      profileMatch && !/\//.test(profileMatch[1]) && !/^0x/i.test(profileMatch[1])
        ? profileMatch[1]
        : null;

    streams.push({
      index,
      kind: kind as MediaStreamInfo['kind'],
      codec,
      profile,
      resolution: /,\s(\d{2,5}x\d{2,5})[\s,]/.exec(`${rest} `)?.[1] ?? null,
      pixelFormat:
        /,\s(yuv[\w]*|rgba?|bgra?|gray|pal8|nv12|yuvj\d+p)[\s,(]/.exec(`${rest} `)?.[1] ?? null,
      frameRate: /,\s([\d.]+)\sfps/.exec(rest)?.[1] ?? null,
      displayAspectRatio: /DAR\s(\d+:\d+)/.exec(rest)?.[1] ?? null,
      sampleRate: /,\s(\d+)\sHz/.exec(rest)?.[1] ?? null,
      channels: /,\s(mono|stereo|\d+\.\d+|\d+\schannels)[\s,]/.exec(`${rest} `)?.[1] ?? null,
      bitrate: /,\s([\d.]+\s[kM]?b\/s)/.exec(rest)?.[1] ?? null,
      language: language && language !== 'und' ? language : null,
    });
  }

  return streams;
}

function getFileExtension(fileName: string, fallback = 'bin') {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || fallback : fallback;
}

export async function inspectMediaFile(file: File): Promise<MediaInspectReport> {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `inspect.${getFileExtension(file.name)}`;
  let log = '';
  const collect = ({ message }: { message: string }) => {
    log += `${message}\n`;
  };

  try {
    runtime.ffmpeg.on('log', collect);
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(file));
    await runtime.ffmpeg.exec(['-i', inputName]);

    const streams = parseStreams(log);
    if (streams.length === 0) {
      throw new Error(
        'No media streams were found in this file. It may be corrupt, or not a media file.',
      );
    }

    return {
      fileName: file.name,
      fileSize: file.size,
      container: parseContainer(log, file.name),
      durationMs: parseDurationMs(log),
      overallBitrate: parseOverallBitrate(log),
      streams,
      tags: parseTags(log),
    };
  } finally {
    runtime.ffmpeg.off('log', collect);
    await runtime.ffmpeg.deleteFile(inputName).catch(() => {});
  }
}
